import {
    type AppointmentChainConfig,
    type ChainStep,
    type StepEligiblePractitioner,
} from '@/models/appointment-chain-config.ts';
import { type PreBookedAppointment } from '@/models/book-collection-response.ts';
import { PrismicLocalizedDoctor } from '@/models/prismic-localized.ts';

/**
 * Steps flagged `is_main_practitioner` — those the patient keeps the same main practitioner across.
 */
export const getActiveMarkedSteps = (config: AppointmentChainConfig): ChainStep[] =>
    config.steps.filter((step) => step.is_main_practitioner);

export const hasActiveMarkedSteps = (config: AppointmentChainConfig): boolean =>
    getActiveMarkedSteps(config).length > 0;

/**
 * Practitioners eligible for EVERY `is_main_practitioner` step — the intersection by
 * `practitioner_id`. Returns the full {@link StepEligiblePractitioner} from the first marked step.
 * `[]` for both "no marked steps" and "no shared practitioner"; callers disambiguate via
 * {@link getActiveMarkedSteps}.
 */
export const getEligibleMainPractitioners = (config: AppointmentChainConfig): StepEligiblePractitioner[] => {
    const markedSteps = getActiveMarkedSteps(config);
    if (markedSteps.length === 0) return [];

    const [firstStep, ...restSteps] = markedSteps;
    // Dedupe by id (first wins) so the result is unique, matching `mapChainToAvailableProfessionals`.
    // `new Map(entries)` would keep the LAST duplicate, so set only when the id is unseen.
    const byPractitionerId = new Map<string, StepEligiblePractitioner>();
    (firstStep.eligible_practitioners ?? []).forEach((practitioner) => {
        if (!byPractitionerId.has(practitioner.practitioner_id)) {
            byPractitionerId.set(practitioner.practitioner_id, practitioner);
        }
    });
    const firstEligible = [...byPractitionerId.values()];

    return firstEligible.filter((practitioner) =>
        restSteps.every((step) =>
            (step.eligible_practitioners ?? []).some(
                (candidate) => candidate.practitioner_id === practitioner.practitioner_id
            )
        )
    );
};

type ResolveBookedChainMainPractitionerArgs = {
    appointments: PreBookedAppointment[];
    professionalProfiles: PrismicLocalizedDoctor[];
    practiceName: string;
    practiceAddress: string | undefined;
    fallbackImageUrl: string;
    chainConfiguration: AppointmentChainConfig;
};

export const resolveBookedChainMainPractitionerProfile = ({
    appointments,
    professionalProfiles,
    practiceName,
    practiceAddress,
    fallbackImageUrl,
    chainConfiguration,
}: ResolveBookedChainMainPractitionerArgs): PrismicLocalizedDoctor & { id: string } => {
    const mainPractitionerStep: ChainStep | undefined = chainConfiguration.steps.find(
        (step) => step.is_main_practitioner
    );
    const mainPractitionerAppointmentTypeId: string | undefined = mainPractitionerStep?.appointment_type_id;
    const mainPractitionerAppointment: PreBookedAppointment | undefined = mainPractitionerAppointmentTypeId
        ? appointments.find((appointment) =>
              appointment.data.services.some((service) => service.id === mainPractitionerAppointmentTypeId)
          )
        : undefined;
    const mainPractitionerProfile: PrismicLocalizedDoctor | undefined = mainPractitionerAppointment
        ? professionalProfiles.find(
              (candidate) => candidate.key?.[0]?.text === mainPractitionerAppointment.professional.key
          )
        : undefined;
    if (mainPractitionerProfile && mainPractitionerAppointment) {
        return { ...mainPractitionerProfile, id: mainPractitionerAppointment.professional.id };
    }

    console.warn(
        '[Appointment chain booking] Could not resolve the main practitioner, falling back to the first practitioner.'
    );
    const firstStep: PreBookedAppointment = [...appointments].sort(
        (a, b) =>
            (a.collection_data?.position ?? Number.POSITIVE_INFINITY) -
            (b.collection_data?.position ?? Number.POSITIVE_INFINITY)
    )[0];

    const fallBackFirstProfessionalProfile: PrismicLocalizedDoctor | undefined = firstStep
        ? professionalProfiles.find((candidate) => candidate.key?.[0]?.text === firstStep.professional.key)
        : undefined;
    if (fallBackFirstProfessionalProfile) {
        return { ...fallBackFirstProfessionalProfile, id: firstStep.professional.id };
    }

    return {
        id: '',
        key: [{ text: '' }],
        display_name: [{ text: practiceName }],
        profile_picture: { url: fallbackImageUrl },
        languages: [],
        practice_name: [{ text: practiceAddress ?? '' }],
    };
};
